import { syncPlanBackground } from './plan_background.mjs';
import { renderActivePlanSurface } from './plan_surface_runtime.mjs';
// @ts-check

import { getPlanCaptureContext, getPlanRenderTarget, reservePlanCaptureNodes } from './plan_capture_context.mjs';

import { NS } from '../utils/common_constants.js?v=20260706d';
import {
  renderSystemOverlays2D,
  renderSystems2D
} from './render_service.js?v=20260904f';
import { drawObstacles2D } from '../obstacles/obstacle_render2d.js?v=20260903c';
import { drawSketchWalls2D } from '../sketch_walls/sketch_wall_render2d.js?v=20260904f';
import {
  appPerfEnd,
  appPerfStep,
  appPerfTimer
} from './render_scheduler.js?v=20260706e';
import {
  isBomPlanCaptureInProgress,
  isBomPlanRendering as isBomPlanContextRendering
} from '../bom/bom_plan_context.js?v=20260904c';
import { isPrintPlanCaptureInProgress } from '../print/print_plan_context.js?v=20260826b';
import {
  beginImportedSvgRenderPass,
  endImportedSvgRenderPass,
  scheduleImportedSvgViewportUpdate,
  shouldForceFullImportedSvgDom
} from '../objects/imported_svg_viewport_runtime.js';
import { worldViewportAabb } from '../objects/imported_svg_virtualization.mjs';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import { obstacleActiveTool } from '../obstacles/obstacles.js?v=20260903c';
import { S } from '../app/app_state.js?v=20260817c';
import { getDrawingObjectRegistryRevision } from '../objects/drawing_object_registry.js?v=20260904f';
import { getProjectModelRevision } from '../project/project_revision_service.js';
import { boundsContain, currentRenderViewportBounds } from './viewport_culling.js?v=20260901a';
import { satelliteFloorplanImageUrl } from '../map/satellite_map_service.js?v=20260706d';
import { isDimensionToolActive, isAngularDimensionToolActive, renderDimensions2D } from '../services/dimension_service.js?v=20260904f';
import { isAreaMeasurementToolActive, renderAreaMeasurements2D } from '../services/area_measurement_service.js?v=20260903c';
import {
  isAutoAreaMeasurementToolActive
} from '../services/auto_area_measurement_service.js?v=20260814c';
import { isTextAnnotationToolActive, renderTextAnnotations2D } from '../services/text_annotation_service.js?v=20260903c';
import {
  PLAN_LAYER_GROUP_IDS,
  PLAN_LAYER_IDS,
  floorplanPlanLayerId,
  isPlanLayerVisible,
  planLayerGroupSvgAttributes,
  planLayerSvgAttributes
} from './plan_layer_service.js?v=20260814b';

/**
 * Beschreibt svg El im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} tag - Eingabewert für svg El.
 * @param {*} attrs - Eingabewert für svg El.
 * @param {*} parent - DOM-, SVG- oder Three.js-Zielcontainer für die Ausgabe.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function svgEl(tag, attrs, parent) {
  reservePlanCaptureNodes();
  const safeAttrs = attrs === undefined ? {} : attrs;
  const el = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(safeAttrs)) {
    if (k === 'transform' && !isValidSvgTransformValue(v)) {
      el.removeAttribute(k);
      continue;
    }
    if (k === 'xlink:href') {
      el.setAttributeNS('http://www.w3.org/1999/xlink', 'href', v);
      continue;
    }
    el.setAttribute(k, v);
  }
  if (parent) parent.appendChild(el);
  return el;
}

function isValidSvgTransformValue(value) {
  if (typeof value !== 'string') return false;
  const transform = value.trim();
  return Boolean(transform) && transform !== 'undefined' && transform !== 'null';
}

/**
 * Beschreibt stop Canvas Mouse Down im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} evt - Eingabewert für stop Canvas Mouse Down.
 * Seiteneffekte: Beachtet fachliche Hilfslogik im jeweiligen Modul; Änderungen erfolgen je nach Aufrufer am übergebenen Objekt oder globalen State.
 */
function stopCanvasMouseDown(evt) {
  evt.stopPropagation();
}

/**
 * Aktualisiert set Status im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Aktualisierung bestehender Daten und abhängiger Berechnungen, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} text - Eingabewert für set Status.
 * Seiteneffekte: Kann Projektstate, Auswahl, Snap-Verbindungen oder abhängige Layoutdaten verändern.
 */
function setStatus(text) {
  if (getPlanCaptureContext()) return;
  const el = document.getElementById('statusText') || document.getElementById('statusbar');
  const status = normalizeCanvasStatusText(text);
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  S.ui.statusText = status;
  if (el) el.textContent = status;
}

/**
 * Normalisiert normalize Canvas Status Text im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt State-Normalisierung, Migration und robuste Defaults, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} text - Eingabewert für normalize Canvas Status Text.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function normalizeCanvasStatusText(text) {
  const status = String(text || '').trim();
  if (!status || status === 'Bereit') return currentCanvasCommandStatusText();
  return status;
}

/**
 * Beschreibt current Canvas Command Status Text im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function currentCanvasCommandStatusText() {
  const obstacleTool = obstacleActiveTool();
  if (obstacleTool === 'pipe') return 'Rohr zeichnen · Punkte nacheinander klicken';
  if (obstacleTool === 'duct') return 'Lüftung zeichnen · Punkte nacheinander klicken';
  const systemStatus = SystemRegistry.getCanvasCommandStatus({ state: S });
  if (systemStatus) return systemStatus;
  if (S.floorplanMeasure?.active) {
    return 'Grundriss neu vermessen · Referenzpunkte im Grundriss anklicken';
  }
  if (isAngularDimensionToolActive()) {
    return 'Winkelbemassung · zwei Kanten anklicken · ESC bricht ab';
  }
  if (isDimensionToolActive()) {
    return 'Bemassung · Punkte oder Kanten anklicken · ESC bricht ab';
  }
  if (isAreaMeasurementToolActive()) {
    return 'Flächenmessung · mindestens drei Eckpunkte anklicken · grüner Haken schließt ab · ESC bricht ab';
  }
  if (isAutoAreaMeasurementToolActive()) {
    return 'Automatische Flächenmessung · in eine geschlossene Fläche klicken · ESC bricht ab';
  }
  if (isTextAnnotationToolActive()) {
    return 'Textfeld · Position im Plan anklicken · ESC bricht ab';
  }
  return 'Bedienung · Linksklick markiert · Ziehen verschiebt oder wählt aus · Mausrad zoomt · Leertaste/Alt + Ziehen verschiebt die Ansicht';
}

/**
 * Beschreibt sync Canvas Status After Render im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
function syncCanvasStatusAfterRender() {
  const el = document.getElementById('statusText') || document.getElementById('statusbar');
  const status = String(el?.textContent || '').trim();
  const isPassiveRenderStatus = (
    !status
    || status === 'Bereit'
    || status.startsWith('Draufsicht ·')
    || status.startsWith('3D-Ansicht ·')
    || status.startsWith('Ansicht ')
  );
  if (isPassiveRenderStatus) setStatus('Bereit');
}

/**
 * Sammelt get Plan Svg im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function getPlanSvg() {
  return getPlanRenderTarget();
}

let cachedCanvasSize = null;
let canvasSizeCacheResetFrame = 0;
/** @type {any} */
let navLayerCache = null;

function measureCanvasSize() {
  const svg = getPlanSvg();
  const r = svg.getBoundingClientRect();
  const wrapRect = document.getElementById('canvasWrap')?.getBoundingClientRect?.();
  const fallbackW = wrapRect?.width || svg.clientWidth || window.innerWidth || document.documentElement.clientWidth || 1;
  const fallbackH = wrapRect?.height || svg.clientHeight || 360;
  return {
    w: Math.max(1, r.width || fallbackW),
    h: Math.max(1, r.height || fallbackH)
  };
}

function scheduleCanvasSizeCacheReset() {
  if (canvasSizeCacheResetFrame) return;
  const resetCache = () => {
    cachedCanvasSize = null;
    canvasSizeCacheResetFrame = 0;
  };
  canvasSizeCacheResetFrame = typeof requestAnimationFrame === 'function'
    ? requestAnimationFrame(resetCache)
    : setTimeout(resetCache, 0);
}

function invalidateCanvasSizeCache() {
  cachedCanvasSize = null;
  canvasSizeCacheResetFrame = 0;
}

/**
 * Sammelt get Canvas Size im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function getCanvasSize() {
  const capture = getPlanCaptureContext();
  if (capture) return { ...capture.size };
  if (!cachedCanvasSize) {
    cachedCanvasSize = measureCanvasSize();
    scheduleCanvasSizeCacheReset();
  }
  return {
    w: cachedCanvasSize.w,
    h: cachedCanvasSize.h
  };
}

/**
 * Beschreibt world To Screen im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} wx - Eingabewert für world To Screen.
 * @param {*} wz - Eingabewert für world To Screen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function worldToScreen(wx, wz) {
  const { w, h } = getCanvasSize();
  const s = S.camera2D.scale;
  return {
    x: (wx - S.camera2D.x) * s + w / 2,
    y: (wz - S.camera2D.z) * s + h / 2
  };
}

/**
 * Beschreibt screen To World im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} sx - Eingabewert für screen To World.
 * @param {*} sy - Eingabewert für screen To World.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function screenToWorld(sx, sy) {
  const { w, h } = getCanvasSize();
  const s = S.camera2D.scale;
  return {
    x: (sx - w / 2) / s + S.camera2D.x,
    z: (sy - h / 2) / s + S.camera2D.z
  };
}

/**
 * Beschreibt world Svg Rect im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} parent - DOM-, SVG- oder Three.js-Zielcontainer für die Ausgabe.
 * @param {*} x - Geometrischer Wert in Projektkoordinaten oder entlang einer lokalen Achse.
 * @param {*} z - Geometrischer Wert in Projektkoordinaten oder entlang einer lokalen Achse.
 * @param {*} width - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @param {*} depth - Eingabewert für world Svg Rect.
 * @param {*} attrs - Eingabewert für world Svg Rect.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function worldSvgRect(parent, x, z, width, depth, attrs = {}) {
  const a = worldToScreen(x, z);
  const b = worldToScreen(x + width, z + depth);
  return svgEl('rect', {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(b.x - a.x),
    height: Math.abs(b.y - a.y),
    ...attrs
  }, parent);
}

/**
 * Beschreibt world Svg Polygon im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} parent - DOM-, SVG- oder Three.js-Zielcontainer für die Ausgabe.
 * @param {*} points - Geometrischer Wert in Projektkoordinaten oder entlang einer lokalen Achse.
 * @param {*} attrs - Eingabewert für world Svg Polygon.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function worldSvgPolygon(parent, points, attrs = {}) {
  return svgEl('polygon', {
    points: points.map(point => {
      const screen = worldToScreen(point.x, point.z);
      return `${screen.x},${screen.y}`;
    }).join(' '),
    ...attrs
  }, parent);
}

/**
 * Rendert render2 D im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
function invalidateNavLayerCache() {
  navLayerCache = null;
}

function render2D({ navOnly = false, overlayOnly = false, dragSystemId = null } = {}) {
  if (!getPlanCaptureContext() && !isBomPlanRendering()
      && renderActivePlanSurface({ navOnly, overlayOnly, dragSystemId })) return;
  renderPlanView({ navOnly, overlayOnly, dragSystemId });
}

function navLayerCacheMatchesCurrentView(cache, w, h, cam, options = {}) {
  if (!cache
      || cache.project !== S.project
      || cache.drawingSheetId !== S.drawingSheets?.activeSheetId
      || cache.svg !== getPlanSvg()
      || cache.capture === true
      || importedSvgCaptureNeedsFullDom(getPlanSvg())
      || isBomPlanRendering()
      || cache.w !== w || cache.h !== h
      || cache.projectRevision !== getProjectModelRevision()
      || cache.drawingObjectRegistryRevision !== getDrawingObjectRegistryRevision()
      || cache.systemRegistryRevision !== SystemRegistry.getSystemRegistryRevision()) return false;
  // Culled layers only cover the viewport (plus padding) of their last full
  // render. Crossing that area during pan/zoom must draw newly visible objects.
  if (options.exactCamera !== true) return boundsContain(cache.renderBounds, currentRenderViewportBounds(0));
  return cache.camera.x === cam.x
    && cache.camera.z === cam.z
    && cache.camera.scale === cam.scale;
}

function renderCorePlanOverlayLayer(parent, { w, h, cam, isBomPlan = false, skipText = false } = {}) {
  if (isBomPlan) return;
  const areaLayer = svgEl('g', planLayerSvgAttributes(
    PLAN_LAYER_IDS.ANNOTATIONS_AREA_MEASUREMENTS,
    S.project,
    { role: 'content', attrs: { class: 'area-measurement-plan-layer' } }
  ), parent);
  if (isPlanLayerVisible(S.project, PLAN_LAYER_IDS.ANNOTATIONS_AREA_MEASUREMENTS)) {
    renderAreaMeasurements2D(areaLayer, {
      svgEl,
      worldToScreen,
      screenToWorld,
      viewportWidth: w,
      viewportHeight: h,
      cameraScale: cam.scale
    });
  }

  const dimensionLayer = svgEl('g', planLayerSvgAttributes(
    PLAN_LAYER_IDS.ANNOTATIONS_LINEAR_DIMENSIONS,
    S.project,
    { role: 'content', attrs: { class: 'linear-dimension-plan-layer' } }
  ), parent);
  if (isPlanLayerVisible(S.project, PLAN_LAYER_IDS.ANNOTATIONS_LINEAR_DIMENSIONS)) {
    renderDimensions2D(dimensionLayer, { svgEl, worldToScreen, screenToWorld });
  }

  if (skipText) return;
  const textLayer = svgEl('g', planLayerSvgAttributes(
    PLAN_LAYER_IDS.ANNOTATIONS_TEXT,
    S.project,
    { role: 'content', attrs: { class: 'text-annotation-plan-layer' } }
  ), parent);
  if (isPlanLayerVisible(S.project, PLAN_LAYER_IDS.ANNOTATIONS_TEXT)) {
    renderTextAnnotations2D(textLayer, { svgEl, worldToScreen, screenToWorld, vectorText: Boolean(getPlanCaptureContext()) });
  }
}

/** Native interaction surface above a GPU scene. No product asset tree is built.
 * Keep the focused text editor mounted: rebuilding it would commit on blur.
 */
export function renderPlanInteractionSurface(options = {}) {
  const svg = getPlanSvg();
  if (!svg || getPlanCaptureContext()) return;
  invalidateNavLayerCache();
  const { w, h } = getCanvasSize(), cam = S.camera2D;
  svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
  let root = svg.querySelector(':scope > .native-plan-interaction');
  if (!root) {
    svg.replaceChildren();
    withImportedSvgRenderPass({ scope: 'plan', sweep: true }, () => undefined);
    root = svgEl('g', { class: 'native-plan-interaction', 'data-dimension-ignore': 'true' }, svg);
  }
  for (const child of [...svg.children]) if (child !== root) child.remove();
  const editor = root.querySelector('.text-annotation-plan-layer');
  const preserveEditor = editor?.contains(document.activeElement);
  for (const child of [...root.children]) if (!preserveEditor || child !== editor) child.remove();
  const product = svgEl('g', { class: 'product-system-overlay-layer' }, root);
  renderSystemOverlays2D(product, { ...options, gpuInteraction: true, strict: true });
  if (isPlanLayerVisible(S.project, PLAN_LAYER_IDS.OBSTACLES_PARAMETRIC_WALLS)) {
    drawSketchWalls2D(svgEl('g', { class: 'sketch-wall-layer' }, root), { gpuWallIds: options.gpuWallIds });
  }
  const core = svgEl('g', { class: 'core-overlay-layer' }, root);
  renderCorePlanOverlayLayer(core, { w, h, cam, skipText: preserveEditor });
  // The editor must survive outside the disposable core layer.
  const text = core.querySelector('.text-annotation-plan-layer');
  if (text) root.append(text);
  if (S.project.floorplan) drawFloorplanMeasureOverlay(svg, S.project.floorplan);
  SystemRegistry.collectDrawingObjectSnapshot();
  syncCanvasStatusAfterRender();
}

function importedSvgViewportState(w, h, reason) {
  return {
    scope: 'plan',
    worldBounds: worldViewportAabb(screenToWorld, w, h),
    reason
  };
}

function importedSvgCaptureNeedsFullDom(svg) {
  // BOM keeps its capture flag set during the final editor restore render.
  // The data attribute, however, exists only for the actual synchronous
  // clone pass, so it prevents leaving the restored editor fully mounted.
  return shouldForceFullImportedSvgDom({
    printCapture: isPrintPlanCaptureInProgress(),
    bomCapture: isBomPlanCaptureInProgress(),
    bomPlanRender: svg?.getAttribute?.('data-bom-plan-render') === 'true'
  });
}

function withImportedSvgRenderPass(passOptions, render) {
  const pass = beginImportedSvgRenderPass(passOptions);
  try {
    return render();
  } finally {
    endImportedSvgRenderPass(pass);
  }
}

/**
 * Rendert render Plan View im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
function renderPlanView(options = {}) {
  renderPlanContent(options);
  syncPlanBackground(getPlanSvg());
}

function renderPlanContent({ navOnly = false, overlayOnly = false, dragSystemId = null } = {}) {
  if (getPlanCaptureContext()) { renderCapturePlanView(); return; }
  const profile = appPerfTimer('plan.renderPlanView');
  const svg = getPlanSvg();
  invalidateCanvasSizeCache();

  const { w, h } = getCanvasSize();
  const cam = S.camera2D;

  // Fast navigation path: skip system/obstacle/overlay rebuild, apply delta transform to cached layers
  if (navOnly && navLayerCacheMatchesCurrentView(navLayerCache, w, h, cam)) {
    svg.innerHTML = '';
    svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
    drawGrid(svg, w, h);
    drawFloorplan(svg);
    const c0 = navLayerCache.camera;
    const scaleRatio = cam.scale / c0.scale;
    const tx = (1 - scaleRatio) * w / 2 + (c0.x - cam.x) * cam.scale;
    const ty = (1 - scaleRatio) * h / 2 + (c0.z - cam.z) * cam.scale;
    const navWrapper = svgEl('g', {
      class: 'nav-layer-cache',
      transform: `matrix(${scaleRatio},0,0,${scaleRatio},${tx},${ty})`
    }, svg);
    navWrapper.appendChild(navLayerCache.systemLayer);
    navWrapper.appendChild(navLayerCache.obstacleLayer);
    navWrapper.appendChild(navLayerCache.sketchWallLayer);
    navWrapper.appendChild(navLayerCache.overlayLayer);

    scheduleImportedSvgViewportUpdate(importedSvgViewportState(w, h, 'navigation'));

    syncCanvasStatusAfterRender();
    appPerfEnd(profile);
    return;
  }

  // Measurement and annotation previews only change the overlay. Reusing the
  // exact-camera base layers keeps pointer movement independent from the number
  // and complexity of product systems in the drawing.
  if (overlayOnly && navLayerCacheMatchesCurrentView(navLayerCache, w, h, cam, { exactCamera: true })) {
    const cached = navLayerCache;
    // With unchanged camera and model, annotation movement only replaces its
    // own content. Keep the grid, floorplan and product DOM attached so the
    // browser does not have to detach and lay out the entire drawing again.
    if (cached.systemLayer.parentNode === svg
        && cached.obstacleLayer.parentNode === svg
        && cached.sketchWallLayer.parentNode === svg
        && cached.overlayLayer.parentNode === svg
        && cached.systemOverlayLayer.parentNode === cached.overlayLayer
        && cached.coreOverlayLayer.parentNode === cached.overlayLayer) {
      // AppController appends interaction handles after this renderer returns.
      // These hooks expect a clean root on the next frame; remove only their
      // additional nodes, retaining every root child from the base render.
      for (const child of [...svg.children]) {
        if (!cached.rootChildren.has(child)) child.remove();
      }
      cached.coreOverlayLayer.replaceChildren();
      renderCorePlanOverlayLayer(cached.coreOverlayLayer, { w, h, cam });
      syncCanvasStatusAfterRender();
      appPerfStep(profile, 'annotations-in-place');
      appPerfEnd(profile);
      return;
    }
    // A preceding navigation render can still own the layers inside its
    // transform wrapper. Restore their untransformed placement once.
    svg.innerHTML = '';
    svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
    drawGrid(svg, w, h);
    drawFloorplan(svg);
    svg.appendChild(navLayerCache.systemLayer);
    svg.appendChild(navLayerCache.obstacleLayer);
    svg.appendChild(navLayerCache.sketchWallLayer);
    const overlayLayer = svgEl('g', { class: 'system-overlay-layer' }, svg);
    overlayLayer.appendChild(navLayerCache.systemOverlayLayer);
    const coreOverlayLayer = svgEl('g', { class: 'core-overlay-layer' }, overlayLayer);
    renderCorePlanOverlayLayer(coreOverlayLayer, { w, h, cam });
    navLayerCache.overlayLayer = overlayLayer;
    navLayerCache.coreOverlayLayer = coreOverlayLayer;
    navLayerCache.rootChildren = new Set(svg.children);
    syncCanvasStatusAfterRender();
    appPerfStep(profile, 'overlay-only');
    appPerfEnd(profile);
    return;
  }

  // Active-system-only path: while a drag is scoped to exactly one product
  // system (options.dragSystemId, set by interaction_controller.js from the
  // hit that started the drag), that system's own placed objects are the
  // only thing that can have changed on screen -- systems never mutate each
  // other's project sub-state (see AGENTS.md's system-independence rule).
  // Reusing every OTHER system's already-rendered <g data-system-id> subtree
  // verbatim skips re-cloning its placed imported-SVG assets every drag
  // frame, which used to dominate render cost on orders with many placed
  // objects. Obstacles/sketch walls/overlays stay cheap enough to rebuild
  // fresh every frame (needed anyway for live selection/snap feedback), so
  // only systemLayer gets the selective-reuse treatment. The camera check
  // mirrors overlayOnly's exactCamera gate -- a drag never pans/zooms at the
  // same time, so camera must be byte-identical to the cached render for the
  // other systems' cached screen coordinates to still be correct.
  if (dragSystemId
      && navLayerCache
      && navLayerCache.project === S.project
      && navLayerCache.drawingSheetId === S.drawingSheets?.activeSheetId
      && navLayerCache.svg === svg
      && navLayerCache.capture !== true
      && !importedSvgCaptureNeedsFullDom(svg)
      && !isBomPlanRendering()
      && navLayerCache.w === w && navLayerCache.h === h
      && navLayerCache.camera.x === cam.x && navLayerCache.camera.z === cam.z && navLayerCache.camera.scale === cam.scale
      && navLayerCache.systemRegistryRevision === SystemRegistry.getSystemRegistryRevision()) {
    const candidateSystemLayer = svgEl('g', { class: 'system-layer' });
    const reuseOk = withImportedSvgRenderPass({ scope: 'plan', sweep: false }, () => {
      const systemsReuseOk = renderSystems2D(candidateSystemLayer, {
        isBomPlan: false,
        onlySystemId: dragSystemId,
        reuseFrom: navLayerCache.systemLayer
      });
      if (!systemsReuseOk) return false;

      svg.innerHTML = '';
      svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
      drawGrid(svg, w, h);
      drawFloorplan(svg);
      svg.appendChild(candidateSystemLayer);

      const obstacleLayer = svgEl('g', planLayerGroupSvgAttributes(
        PLAN_LAYER_GROUP_IDS.OBSTACLES,
        S.project,
        { role: 'content', attrs: { class: 'obstacle-layer-root' } }
      ), svg);
      const sketchWallLayer = svgEl('g', planLayerSvgAttributes(
        PLAN_LAYER_IDS.OBSTACLES_PARAMETRIC_WALLS,
        S.project,
        { role: 'content', attrs: { class: 'sketch-wall-layer-root' } }
      ), svg);
      drawObstacles2D(obstacleLayer);
      if (isPlanLayerVisible(S.project, PLAN_LAYER_IDS.OBSTACLES_PARAMETRIC_WALLS)) {
        drawSketchWalls2D(sketchWallLayer);
      }

      const overlayLayer = svgEl('g', { class: 'system-overlay-layer' }, svg);
      const systemOverlayLayer = svgEl('g', { class: 'product-system-overlay-layer' }, overlayLayer);
      const coreOverlayLayer = svgEl('g', { class: 'core-overlay-layer' }, overlayLayer);
      renderSystemOverlays2D(systemOverlayLayer, { isBomPlan: false });
      renderCorePlanOverlayLayer(coreOverlayLayer, { w, h, cam, isBomPlan: false });

      SystemRegistry.collectDrawingObjectSnapshot();

      navLayerCache = {
        rootChildren: new Set(svg.children),
        project: S.project,
        drawingSheetId: S.drawingSheets?.activeSheetId,
        svg,
        systemLayer: candidateSystemLayer,
        obstacleLayer,
        sketchWallLayer,
        overlayLayer,
        systemOverlayLayer,
        coreOverlayLayer,
        camera: { x: cam.x, z: cam.z, scale: cam.scale },
        renderBounds: navLayerCache.renderBounds,
        capture: false,
        w,
        h,
        projectRevision: getProjectModelRevision(),
        drawingObjectRegistryRevision: getDrawingObjectRegistryRevision(),
        systemRegistryRevision: SystemRegistry.getSystemRegistryRevision()
      };
      return true;
    });
    if (reuseOk) {
      syncCanvasStatusAfterRender();
      appPerfStep(profile, 'active-system-only');
      appPerfEnd(profile);
      return;
    }
    // A system had no cached layer to reuse (e.g. right after a fresh full
    // render never happened yet) -- fall through to the full render below
    // instead of committing an incomplete plan.
  }

  // Full render path
  svg.innerHTML = '';
  appPerfStep(profile, 'clear-svg');

  svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
  appPerfStep(profile, 'viewport');

  drawGrid(svg, w, h);
  appPerfStep(profile, 'grid');
  drawFloorplan(svg);
  appPerfStep(profile, 'floorplan');
  const systemLayer = svgEl('g', { class: 'system-layer' }, svg);
  const obstacleLayer = svgEl('g', planLayerGroupSvgAttributes(
    PLAN_LAYER_GROUP_IDS.OBSTACLES,
    S.project,
    { role: 'content', attrs: { class: 'obstacle-layer-root' } }
  ), svg);
  const sketchWallLayer = svgEl('g', planLayerSvgAttributes(
    PLAN_LAYER_IDS.OBSTACLES_PARAMETRIC_WALLS,
    S.project,
    { role: 'content', attrs: { class: 'sketch-wall-layer-root' } }
  ), svg);
  const overlayLayer = svgEl('g', { class: 'system-overlay-layer' }, svg);
  const systemOverlayLayer = svgEl('g', { class: 'product-system-overlay-layer' }, overlayLayer);
  const coreOverlayLayer = svgEl('g', { class: 'core-overlay-layer' }, overlayLayer);
  const isBomPlan = isBomPlanRendering();
  appPerfStep(profile, 'layers');

  const forceFullImportedSvg = importedSvgCaptureNeedsFullDom(svg);
  withImportedSvgRenderPass({
    scope: forceFullImportedSvg ? 'capture' : 'plan',
    forceFull: forceFullImportedSvg,
    sweep: true
  }, () => {
    renderSystems2D(systemLayer, { isBomPlan });
    appPerfStep(profile, 'systems');

    if (!isBomPlan) {
      drawObstacles2D(obstacleLayer);
      if (isPlanLayerVisible(S.project, PLAN_LAYER_IDS.OBSTACLES_PARAMETRIC_WALLS)) {
        drawSketchWalls2D(sketchWallLayer);
      }
    }
    appPerfStep(profile, 'obstacles');

    if (!isBomPlan) renderSystemOverlays2D(systemOverlayLayer, { isBomPlan });
  });
  renderCorePlanOverlayLayer(coreOverlayLayer, { w, h, cam, isBomPlan });
  appPerfStep(profile, 'system-overlays');

  // Gemeinsamen DrawingObjectSnapshot vor dem Speichern der Registry-Revision
  // aufbauen. Die direkt folgenden Auswahl-Overlays verwenden dadurch denselben
  // Snapshot, ohne den Navigations-Layer-Cache nachtraeglich zu invalidieren.
  SystemRegistry.collectDrawingObjectSnapshot();
  appPerfStep(profile, 'drawing-object-snapshot');

  // Save data layers for subsequent navigation renders
  navLayerCache = {
    rootChildren: new Set(svg.children),
    project: S.project,
    drawingSheetId: S.drawingSheets?.activeSheetId,
    svg,
    systemLayer,
    obstacleLayer,
    sketchWallLayer,
    overlayLayer,
    systemOverlayLayer,
    coreOverlayLayer,
    camera: { x: cam.x, z: cam.z, scale: cam.scale },
    renderBounds: currentRenderViewportBounds(),
    capture: forceFullImportedSvg || isBomPlan,
    w,
    h,
    projectRevision: getProjectModelRevision(),
    drawingObjectRegistryRevision: getDrawingObjectRegistryRevision(),
    systemRegistryRevision: SystemRegistry.getSystemRegistryRevision()
  };
  syncCanvasStatusAfterRender();
  appPerfStep(profile, 'status');
  appPerfEnd(profile);
}

// Separate output tree: no live DOM, navigation cache, status update or snapshot publication.
function renderCapturePlanView() {
  const svg = getPlanSvg(), { w, h } = getCanvasSize(), cam = S.camera2D;
  svg.replaceChildren();
  svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
  drawGrid(svg, w, h);
  drawFloorplan(svg);
  const systemLayer = svgEl('g', { class: 'system-layer' }, svg);
  const obstacles = svgEl('g', planLayerGroupSvgAttributes(PLAN_LAYER_GROUP_IDS.OBSTACLES, S.project, { role: 'content', attrs: { class: 'obstacle-layer-root' } }), svg);
  const walls = svgEl('g', planLayerSvgAttributes(PLAN_LAYER_IDS.OBSTACLES_PARAMETRIC_WALLS, S.project, { role: 'content', attrs: { class: 'sketch-wall-layer-root' } }), svg);
  const overlays = svgEl('g', { class: 'system-overlay-layer' }, svg);
  const productOverlay = svgEl('g', { class: 'product-system-overlay-layer' }, overlays);
  const coreOverlay = svgEl('g', { class: 'core-overlay-layer' }, overlays);
  const isBomPlan = isBomPlanRendering();
  withImportedSvgRenderPass({ scope: 'capture', forceFull: true, isolated: true, sweep: true }, () => {
    renderSystems2D(systemLayer, { isBomPlan, strict: true });
    if (!isBomPlan) {
      drawObstacles2D(obstacles);
      if (isPlanLayerVisible(S.project, PLAN_LAYER_IDS.OBSTACLES_PARAMETRIC_WALLS)) drawSketchWalls2D(walls);
      renderSystemOverlays2D(productOverlay, { isBomPlan, strict: true });
    }
    renderCorePlanOverlayLayer(coreOverlay, { w, h, cam, isBomPlan });
  });
}

/**
 * Prüft is Bom Plan Rendering im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * - Verdichtet technische Artikelinformationen für Anzeige, Export oder Stückliste.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function isBomPlanRendering() {
  return isBomPlanContextRendering();
}

/**
 * Rendert draw Grid im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} svg - Eingabewert für draw Grid.
 * @param {*} w - Eingabewert für draw Grid.
 * @param {*} h - Eingabewert für draw Grid.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
function drawGrid(svg, w, h) {
  const grid = S.project.gridSize;
  const numericHelperGrid = Number(S.project.helperGridSize);
  const helperGrid = Number.isFinite(numericHelperGrid) ? numericHelperGrid : 0;
  const p0 = screenToWorld(0, 0);
  const p1 = screenToWorld(w, h);
  const displayGrid = visibleGridStep(grid);
  const displayHelperGrid = visibleGridStep(helperGrid);

  if (S.project.showHelperGrid && displayHelperGrid > 0) {
    drawGridLines(svg, p0, p1, displayHelperGrid, {
      opacity: '0.55',
      stroke: '#fb7185',
      axisStroke: '#e11d48',
      strokeWidth: 0.8,
      axisStrokeWidth: 1.1
    });
  }

  if (S.project.showGrid && displayGrid > 0) {
    drawGridLines(svg, p0, p1, displayGrid, {
      opacity: '0.8',
      stroke: '#d1d5db',
      axisStroke: '#94a3b8',
      strokeWidth: 1,
      axisStrokeWidth: 1.4
    });
  }
}

function visibleGridStep(grid) {
  const numericGrid = Number(grid);
  const base = Number.isFinite(numericGrid) ? Math.max(1, numericGrid) : 0;
  if (!base) return 0;
  const numericScale = Number(S?.camera2D?.scale);
  const scale = Number.isFinite(numericScale) ? Math.max(0.001, numericScale) : 1;
  const minPixelSpacing = 8;
  const factor = Math.max(1, Math.ceil(minPixelSpacing / (base * scale)));
  return base * factor;
}

/**
 * Rendert draw Grid Lines im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} svg - Eingabewert für draw Grid Lines.
 * @param {*} p0 - Eingabewert für draw Grid Lines.
 * @param {*} p1 - Eingabewert für draw Grid Lines.
 * @param {*} grid - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @param {*} style - Eingabewert für draw Grid Lines.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
function drawGridLines(svg, p0, p1, grid, style) {
  const minX = Math.floor(Math.min(p0.x, p1.x) / grid) * grid;
  const maxX = Math.ceil(Math.max(p0.x, p1.x) / grid) * grid;
  const minZ = Math.floor(Math.min(p0.z, p1.z) / grid) * grid;
  const maxZ = Math.ceil(Math.max(p0.z, p1.z) / grid) * grid;

  const g = svgEl('g', { opacity: style.opacity }, svg);
  const regularParts = [];
  const axisParts = [];

  for (let x = minX; x <= maxX; x += grid) {
    const a = worldToScreen(x, minZ);
    const b = worldToScreen(x, maxZ);
    const segment = `M${a.x},${a.y}L${b.x},${b.y}`;
    (x === 0 ? axisParts : regularParts).push(segment);
  }

  for (let z = minZ; z <= maxZ; z += grid) {
    const a = worldToScreen(minX, z);
    const b = worldToScreen(maxX, z);
    const segment = `M${a.x},${a.y}L${b.x},${b.y}`;
    (z === 0 ? axisParts : regularParts).push(segment);
  }

  if (regularParts.length) {
    svgEl('path', {
      d: regularParts.join(' '),
      fill: 'none',
      stroke: style.stroke,
      'stroke-width': style.strokeWidth,
      'pointer-events': 'none'
    }, g);
  }
  if (axisParts.length) {
    svgEl('path', {
      d: axisParts.join(' '),
      fill: 'none',
      stroke: style.axisStroke,
      'stroke-width': style.axisStrokeWidth,
      'pointer-events': 'none'
    }, g);
  }
}

/**
 * Rendert draw Floorplan im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} svg - Eingabewert für draw Floorplan.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
function drawFloorplan(svg) {
  const fp = S.project.floorplan;
  if (!fp || fp.visible === false) return;
  const isSatellite = String(fp.kind || fp.backgroundType || '').trim().toLowerCase() === 'satellite';
  const href = fp.url || (isSatellite ? satelliteFloorplanImageUrl(fp) : '');
  if (!href) return;

  const topLeft = worldToScreen(fp.x, fp.z);
  const size = {
    w: fp.width * S.camera2D.scale,
    h: fp.height * S.camera2D.scale
  };

  const layerId = floorplanPlanLayerId(fp);
  const layer = svgEl('g', planLayerSvgAttributes(layerId, S.project, {
    role: 'content',
    attrs: {
      class: `background-layer floorplan-background-layer${isSatellite ? ' satellite-background-layer' : ''}`
    }
  }), svg);
  if (!isPlanLayerVisible(S.project, layerId)) return;
  svgEl('image', {
    href,
    'xlink:href': href,
    x: topLeft.x,
    y: topLeft.y,
    width: size.w,
    height: size.h,
    opacity: fp.opacity ?? 0.85,
    preserveAspectRatio: 'none',
    class: `floorplan-background${isSatellite ? ' satellite-background' : ''}${fp.locked ? ' is-locked' : ''}`,
    'data-floorplan-background': 'true',
    'data-background-type': isSatellite ? 'satellite' : 'floorplan',
    'pointer-events': (fp.locked || isSatellite) ? 'none' : 'visiblePainted'
  }, layer);

  drawFloorplanMeasureOverlay(layer, fp);
}

/**
 * Rendert draw Floorplan Measure Overlay im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Hilfslogik.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} svg - Eingabewert für draw Floorplan Measure Overlay.
 * @param {*} fp - Eingabewert für draw Floorplan Measure Overlay.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
function drawFloorplanMeasureOverlay(svg, fp) {
  const measure = S.floorplanMeasure;
  if (!measure?.active) return;

  const a = worldToScreen(fp.x, fp.z);
  const b = worldToScreen(fp.x + fp.width, fp.z + fp.height);
  svgEl('rect', {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(b.x - a.x),
    height: Math.abs(b.y - a.y),
    fill: 'none',
    stroke: '#dc2626',
    'stroke-width': 2,
    'stroke-dasharray': '10 6',
    'pointer-events': 'none'
  }, svg);

  const points = measure.points || [];
  const screenPoints = points.map(p => worldToScreen(p.x, p.z));
  if (screenPoints.length === 2) {
    svgEl('line', {
      x1: screenPoints[0].x,
      y1: screenPoints[0].y,
      x2: screenPoints[1].x,
      y2: screenPoints[1].y,
      stroke: '#dc2626',
      'stroke-width': 3,
      'pointer-events': 'none'
    }, svg);
  }

  for (const p of screenPoints) {
    svgEl('circle', {
      cx: p.x,
      cy: p.y,
      r: 6,
      fill: '#dc2626',
      stroke: '#ffffff',
      'stroke-width': 2,
      'pointer-events': 'none'
    }, svg);
  }
}

export {
  svgEl,
  isValidSvgTransformValue,
  stopCanvasMouseDown,
  setStatus,
  normalizeCanvasStatusText,
  currentCanvasCommandStatusText,
  syncCanvasStatusAfterRender,
  getPlanSvg,
  getCanvasSize,
  worldToScreen,
  screenToWorld,
  worldSvgRect,
  worldSvgPolygon,
  invalidateNavLayerCache,
  render2D,
  renderPlanView,
  isBomPlanRendering,
  drawGrid,
  visibleGridStep,
  drawGridLines,
  drawFloorplan,
  drawFloorplanMeasureOverlay
};
